import { AuditedTextarea } from './AuditedTextarea';
import { useEffect, useId, useRef, useState } from 'react';
import { Mic, Square, X, Check, LoaderCircle } from 'lucide-react';
import { request, flushPastes } from './api';
import { ErrorNotice } from './components';
import { fieldPasteParents, fieldPasteRanges, restoreFieldPasteRanges, flushAccountInputPastes, type CorrectionRange } from './accountPasteAudit';

export function VoiceInput({
  sessionId,
  available,
  onUse,
  label = 'Think aloud',
  disabled = false,
  transcriptionPath,
  onStart,
  initialTranscript,
  unavailableReason = 'Voice is unavailable. Check the service and assignment rules; you can keep typing.',
}: {
  sessionId: string;
  available: boolean;
  onUse: (text: string, transcriptId: string, parents?: {parentPasteIds: string[]; parentInputPasteIds: string[]; correctionRanges: CorrectionRange[]}) => void | Promise<void>;
  label?: string;
  disabled?: boolean;
  transcriptionPath?: string;
  onStart?: () => boolean | Promise<boolean>;
  initialTranscript?: {id: string; text: string; correctionRanges?: CorrectionRange[]};
  unavailableReason?: string;
}) {
  const [state, setState] = useState<'idle' | 'recording' | 'processing' | 'review'>(initialTranscript ? 'review' : 'idle');
  const [text, setText] = useState(initialTranscript?.text || '');
  const [id, setId] = useState(initialTranscript?.id || '');
  // Recovery acknowledgement is not a recorder lifecycle change. Only the
  // untouched review restored on mount may be cleared when its copy disappears.
  const restoredReview = useRef(initialTranscript ? {id: initialTranscript.id, text: initialTranscript.text} : undefined);
  const reviewId = useId();
  const [confirming, setConfirming] = useState(false);
  useEffect(() => {
    const restored = restoredReview.current;
    if (!restored || initialTranscript?.id === restored.id || confirming) return;
    restoredReview.current = undefined;
    if (state === 'review' && id === restored.id && text === restored.text) {
      setText(''); setId(''); setState('idle');
    }
  }, [initialTranscript?.id, confirming, state, id, text]);
  const reviewField = useRef<HTMLTextAreaElement | null>(null);
  const reviewHistory = useRef([{text:initialTranscript?.text || '',ranges:initialTranscript?.correctionRanges || [] as CorrectionRange[]}]);
  const reviewCursor = useRef(0);
  useEffect(() => {
    if (state==='review' && reviewField.current && reviewCursor.current===0 && initialTranscript?.correctionRanges)
      restoreFieldPasteRanges(reviewField.current, text, initialTranscript.correctionRanges);
  }, []);
  function changed(field: HTMLTextAreaElement) {
    const next={text:field.value,ranges:fieldPasteRanges(field)};
    reviewHistory.current=reviewHistory.current.slice(0,reviewCursor.current+1);
    reviewHistory.current.push(next);
    if (reviewHistory.current.length>200) reviewHistory.current.shift();
    reviewCursor.current=reviewHistory.current.length-1;
    setText(next.text);
  }
  function undoReview(redo: boolean) {
    const next=reviewCursor.current+(redo?1:-1), field=reviewField.current;
    if (!field || confirming || next<0 || next>=reviewHistory.current.length) return;
    reviewCursor.current=next;
    const saved=reviewHistory.current[next];
    restoreFieldPasteRanges(field,saved.text,saved.ranges);setText(saved.text);
  }
  const reviewKeys = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((event.ctrlKey || event.metaKey) && ['z','y'].includes(event.key.toLowerCase())) {
      event.preventDefault();undoReview(event.key.toLowerCase()==='y' || event.shiftKey);
    }
  };
  const reviewInput = (event: React.FormEvent<HTMLTextAreaElement>) => {
    const type=(event.nativeEvent as InputEvent).inputType;
    if (type==='historyUndo' || type==='historyRedo') {event.preventDefault();undoReview(type==='historyRedo');}
  };
  const [error, setError] = useState('');
  const [seconds, setSeconds] = useState(0);
  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const cancelled = useRef(false);
  const attempt = useRef(0);
  const mounted = useRef(true);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const controller = useRef<AbortController | null>(null);
  const stopTracks = () => {
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
    if (timer.current) clearInterval(timer.current);
  };
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      cancelled.current = true;
      attempt.current++;
      controller.current?.abort();
      if (recorder.current?.state === 'recording') recorder.current.stop();
      stopTracks();
    };
  }, []);
  async function start() {
    if (disabled || !available || confirming) return;
    setError('');
    restoredReview.current = undefined;
    cancelled.current = false;
    const startedAttempt = ++attempt.current;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError(
        'Recording needs a supported browser on HTTPS or localhost. You can type your thought instead.',
      );
      return;
    }
    try {
      setState('processing');
      if (onStart && !(await onStart())) { setState('idle'); return; }
      if (!mounted.current || cancelled.current || attempt.current !== startedAttempt) return;
      const media = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
        video: false,
      });
      if (!mounted.current || cancelled.current || attempt.current !== startedAttempt) {
        media.getTracks().forEach((track) => track.stop());
        return;
      }
      stream.current = media;
      const mimeType = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/webm'].find((type) =>
        MediaRecorder.isTypeSupported(type),
      );
      const recording = new MediaRecorder(media, mimeType ? { mimeType } : undefined);
      recorder.current = recording;
      const chunks: Blob[] = [];
      recording.ondataavailable = (event) => {
        if (event.data.size) chunks.push(event.data);
      };
      recording.onerror = () => {
        cancelled.current = true;
        chunks.length = 0;
        stopTracks();
        setState('idle');
        setError('The recording was interrupted. Please try again or type.');
      };
      recording.onstop = async () => {
        stopTracks();
        if (recorder.current === recording) recorder.current = null;
        if (cancelled.current || !mounted.current || attempt.current !== startedAttempt) { chunks.length=0; return; }
        setState('processing');
        try {
          const blob = new Blob(chunks, {
            type: recording.mimeType || mimeType || 'audio/webm',
          });
          chunks.length=0;
          controller.current = new AbortController();
          const response = await request(transcriptionPath || `/sessions/${sessionId}/transcriptions`, {
            method: 'POST',
            body: blob,
            headers: { 'Content-Type': blob.type },
            signal: controller.current.signal,
          });
          const result = await response.json();
          if (!mounted.current || cancelled.current || attempt.current !== startedAttempt) return;
          setText(result.text);
          reviewHistory.current=[{text:result.text,ranges:[]}];reviewCursor.current=0;
          setId(result.id);
          setState('review');
        } catch (cause) {
          if (mounted.current && !cancelled.current) {
            setError((cause as Error).message);
            setState('idle');
          }
        }
      };
      recording.start();
      setSeconds(0);
      setState('recording');
      let elapsed = 0;
      timer.current = setInterval(() => {
        elapsed += 1;
        setSeconds(elapsed);
        if (elapsed >= 60 && recording.state === 'recording') recording.stop();
      }, 1000);
    } catch {
      if (!mounted.current || cancelled.current || attempt.current !== startedAttempt) return;
      stopTracks();
      setError(
        'Microphone access was not available. Check your browser permission, or type instead.',
      );
      setState('idle');
    }
  }
  function cancel() {
    if (confirming) return;
    restoredReview.current = undefined;
    cancelled.current = true;
    attempt.current++;
    controller.current?.abort();
    if (recorder.current?.state === 'recording') recorder.current.stop();
    stopTracks();
    setText('');
    setState('idle');
  }
  return (
    <div className={`voice-input voice-${state}`}>
      {state === 'idle' && (
        <button type="button"
          className="voice-button"
          onClick={start}
          disabled={disabled || !available}
          title={
            !available
              ? unavailableReason
              : 'Record up to 60 seconds, then review your words'
          }
        >
          <Mic size={17} />
          {label}
        </button>
      )}
      {state === 'recording' && (
        <div className="recording-bar">
          <span className="recording-dot" />
          <span>Listening · {seconds}s / 60s</span>
          <button type="button" className="small-button" onClick={() => recorder.current?.stop()}>
            <Square size={14} />
            Finish
          </button>
          <button type="button" className="icon-button" aria-label="Cancel recording" onClick={cancel}>
            <X size={17} />
          </button>
        </div>
      )}
      {state === 'processing' && (
        <div className="recording-bar">
          <LoaderCircle size={16} className="spin" />
          <span>Listening back…</span>
          <button type="button" className="icon-button" aria-label="Cancel transcription" onClick={cancel}>
            <X size={17} />
          </button>
        </div>
      )}
      {state === 'review' && (
        <div className="transcript-review">
          <label htmlFor={reviewId}>Did I hear you correctly?</label>
          {sessionId ? <AuditedTextarea ref={reviewField} sessionId={sessionId} destination="transcript"
            id={reviewId}
            value={text}
            maxLength={4000}
            disabled={confirming} onKeyDown={reviewKeys} onBeforeInput={reviewInput}
            onChange={(event) => changed(event.currentTarget)}
            rows={3}
          /> : <textarea ref={reviewField} id={reviewId}
            aria-label="Transcript correction" value={text} maxLength={4000}
            disabled={confirming} onKeyDown={reviewKeys} onBeforeInput={reviewInput}
            onChange={event => changed(event.currentTarget)} rows={3} />}
          <p>
            Correct anything misheard before using this text. The learning record keeps the original
            transcript and your confirmed version.
          </p>
          <div className="button-row">
            <button type="button"
              className="small-button primary"
              disabled={!text.trim() || confirming || disabled}
              onClick={async () => {
                if (confirming || disabled) return;
                setConfirming(true);
                setError('');
                try {
                  const trimStart=text.length-text.trimStart().length, trimEnd=text.trimEnd().length;
                  const parents = reviewField.current ? {...fieldPasteParents(reviewField.current, trimStart, trimEnd),
                    correctionRanges:fieldPasteRanges(reviewField.current,trimStart,trimEnd)} : undefined;
                  if (!sessionId) await flushAccountInputPastes();
                  else await flushPastes(sessionId);
                  await onUse(text.trim(), id, parents);
                  if (!mounted.current) return;
                  restoredReview.current = undefined;
                  setState('idle');
                  setText('');
                } catch (cause) {
                  if (mounted.current) setError((cause as Error).message);
                } finally {
                  if (mounted.current) setConfirming(false);
                }
              }}
            >
              <Check size={15} />
              {confirming ? 'Confirming…' : 'Use transcript'}
            </button>
            <button type="button" className="text-button" disabled={confirming} onClick={cancel}>
              Discard
            </button>
          </div>
        </div>
      )}
      {error && <ErrorNotice message={error} />}
    </div>
  );
}
